import type { ITenantDestination } from '@/entities/tenant-destination'
import type { ICountry, IProvince } from '@/shared/api/types/country-types'
import type { IPOI, IPoiCategory } from '@/shared/api/types/poi-types'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Plus, Tags } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { useCountriesQuery, useProvincesQuery } from '@/entities/country'
import {
  POI_QUERY_KEYS,
  usePoiCategoriesInfiniteQuery,
  useTenantPoisQuery,
} from '@/entities/poi/api/poi-queries'
import {
  TENANT_DESTINATION_QUERY_KEYS,
  useTenantDestinationsQuery,
} from '@/entities/tenant-destination'
import { PoiListTable } from '@/features/poi/poi-list-table'
import { TenantSearchBar } from '@/features/tenant/tenant-search-bar'
import { DEFAULT_FETCH_LIMIT, DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { POI_CREATED_SOURCE } from '@/shared/constants/poi-created-source'
import type { PoiCreatedSource } from '@/shared/constants/poi-created-source'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { useTenantListScope } from '@/shared/hooks/use-tenant-list-scope'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'

interface OptionItem {
  label: string
  value: string
}

const POI_FILTER_VALUE = {
  ALL: '__all__',
} as const

const POI_STATUS_FILTER = {
  ALL: POI_FILTER_VALUE.ALL,
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const

type PoiStatusFilter = (typeof POI_STATUS_FILTER)[keyof typeof POI_STATUS_FILTER]

/**
 * Renders the tenant POI list with localized data and advanced tenant filters.
 */
export function PoiListPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const {
    isSuperAdmin,
    setActiveTenantId,
    tenantOptions,
    currentTenantId,
    currentTenantName: tenantName,
    selectedLanguageCode,
    setSelectedLanguageCode,
    languageOptions,
    defaultLanguageCode,
    effectiveLanguageCode,
    isLanguagesReady,
    handleLanguageScroll,
  } = useTenantListScope()
  const [searchTerm, setSearchTerm] = useState('')
  const [category, setCategory] = useState('')
  const [statusFilter, setStatusFilter] = useState<PoiStatusFilter>(POI_STATUS_FILTER.ACTIVE)
  const [createdSource, setCreatedSource] = useState<PoiCreatedSource | ''>('')
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedProvinceId, setSelectedProvinceId] = useState('')
  const [selectedTenantDestinationId, setSelectedTenantDestinationId] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // * VI: Hook này luôn gọi endpoint POI theo tenant context để bộ lọc/list không bị lẫn dữ liệu global của superadmin. | EN: This hook always uses tenant-scoped POI endpoint so list/filter data never mixes with superadmin-global data.
  const { data, isLoading } = useTenantPoisQuery(
    currentTenantId,
    {
      current: page,
      pageSize,
      ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
      isActive:
        statusFilter === POI_STATUS_FILTER.ACTIVE
          ? true
          : statusFilter === POI_STATUS_FILTER.INACTIVE
            ? false
            : undefined,
      ...(category ? { category } : {}),
      ...(selectedProvinceId ? { provinceId: selectedProvinceId } : {}),
      ...(selectedTenantDestinationId ? { tenantDestinationId: selectedTenantDestinationId } : {}),
      ...(searchTerm.trim() ? { keyword: searchTerm.trim() } : {}),
      ...(createdSource ? { createdSource } : {}),
    },
    { enabled: isLanguagesReady },
  )

  const pois = data?.data ?? []
  const total = data?.total ?? 0

  const { data: countriesResponse } = useCountriesQuery({})
  const { data: provincesResponse } = useProvincesQuery({
    countryId: selectedCountryId,
  })
  const { data: destinationsResponse } = useTenantDestinationsQuery(
    {
      current: 1,
      pageSize: DEFAULT_FETCH_LIMIT,
      ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
      isActive: true,
    },
    { enabled: isLanguagesReady },
  )

  const poiCategoriesQuery = usePoiCategoriesInfiniteQuery(
    currentTenantId,
    {
      pageSize: 20,
      ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
    },
    { enabled: isLanguagesReady },
  )
  const poiCategories = useMemo<IPoiCategory[]>(
    () => poiCategoriesQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [poiCategoriesQuery.data],
  )

  const countryItems: ICountry[] = Array.isArray(countriesResponse)
    ? countriesResponse
    : (countriesResponse?.data ?? [])

  const provinceItems: IProvince[] = Array.isArray(provincesResponse)
    ? provincesResponse
    : (provincesResponse?.data ?? [])

  const countryOptions = countryItems.map((country) => ({
    label: country.nativeName ?? country.name ?? country.code,
    value: country.id,
  }))

  const provinceOptions = provinceItems.map((province) => ({
    label: province.nativeName ?? province.name ?? province.code,
    value: province.id,
  }))
  const destinationOptions = (destinationsResponse?.data?.data ?? []).map(
    (destination: ITenantDestination) => ({
      label: destination.localizedData?.[effectiveLanguageCode]?.name ?? 'No content available',
      value: destination.id,
    }),
  )
  const activeFilterCount = [
    selectedLanguageCode && selectedLanguageCode !== defaultLanguageCode
      ? selectedLanguageCode
      : '',
    selectedCountryId,
    selectedProvinceId,
    selectedTenantDestinationId,
    category,
    statusFilter !== POI_STATUS_FILTER.ALL ? statusFilter : '',
    createdSource,
  ].filter(Boolean).length

  const categoryOptions = useMemo(() => {
    const options = new Map<string, { label: string; value: string; icon?: string }>()
    for (const item of poiCategories) {
      const value = item.id.trim()
      if (!value) {
        continue
      }
      const label = resolvePoiCategoryName(
        item,
        Array.from(new Set([selectedLanguageCode, defaultLanguageCode].filter(Boolean))),
      )
      if (label) {
        options.set(value, {
          value,
          label,
          ...(item.icon ? { icon: item.icon } : {}),
        })
      }
    }
    const normalizedCategory = category.trim()
    if (normalizedCategory && !options.has(normalizedCategory)) {
      options.set(normalizedCategory, { value: normalizedCategory, label: normalizedCategory })
    }
    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [category, defaultLanguageCode, poiCategories, selectedLanguageCode])

  const handleCategoryScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 40
      if (nearBottom && poiCategoriesQuery.hasNextPage && !poiCategoriesQuery.isFetchingNextPage) {
        void poiCategoriesQuery.fetchNextPage()
      }
    },
    [poiCategoriesQuery],
  )

  /**
   * Applies table pagination changes to the POI list query state.
   */
  function handlePageChange(nextPage: number, nextPageSize: number) {
    setPage(nextPage)
    setPageSize(nextPageSize)
  }

  /**
   * Opens the selected POI edit page while preserving the active language.
   */
  function handleEdit(poi: IPOI) {
    if (!currentTenantId) return
    navigate({
      to: scopedAdminPath(
        generatePath('/tenant/:tenantId/pois/:poiId/edit', {
          tenantId: currentTenantId,
          poiId: poi.id,
        }),
      ) as never,
      ...(selectedLanguageCode ? { search: { lang: selectedLanguageCode } as never } : {}),
    }) as never
  }

  /**
   * Restores POI filters to defaults for the active tenant and language.
   */
  function resetAdvancedFilters() {
    setSelectedLanguageCode(defaultLanguageCode)
    setSelectedCountryId('')
    setSelectedProvinceId('')
    setSelectedTenantDestinationId('')
    setCategory('')
    setStatusFilter(POI_STATUS_FILTER.ACTIVE)
    setCreatedSource('')
    setPage(1)
  }

  /**
   * Switches the active tenant and resets the filters/queries scoped to the previous tenant.
   */
  function handleTenantChange(tenantId: string) {
    setActiveTenantId(tenantId)
    setPage(1)
    setSelectedLanguageCode('')
    setSelectedTenantDestinationId('')
    setCategory('')
    void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all })
    void queryClient.invalidateQueries({ queryKey: TENANT_DESTINATION_QUERY_KEYS.all })
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('poi.title')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(tenantName
            ? [
                {
                  label: tenantName,
                  path: scopedAdminPath(
                    generatePath('/tenant/:tenantId', { tenantId: currentTenantId }),
                  ),
                },
              ]
            : []),
          { label: t('poi.title') },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!currentTenantId}
              onClick={async () =>
                navigate({
                  to: scopedAdminPath(
                    generatePath('/tenant/:tenantId/poi-categories', { tenantId: currentTenantId }),
                  ) as never,
                })
              }
            >
              <Tags className="mr-1.5 h-4 w-4" />
              {t('poiCategories.openManagement', { defaultValue: 'Manage POI categories' })}
            </Button>
            <Button
              disabled={!currentTenantId}
              onClick={async () =>
                navigate({
                  to: scopedAdminPath(
                    generatePath('/tenant/:tenantId/pois/create', { tenantId: currentTenantId }),
                  ) as never,
                })
              }
            >
              <Plus className="mr-1.5 h-4 w-4" />
              {t('poi.create')}
            </Button>
          </div>
        }
      />

      <DataTableToolbar>
        {isSuperAdmin && (
          <DataTableFilterField label={t('sidebar.tenants')}>
            <Select
              {...(currentTenantId ? { value: currentTenantId } : {})}
              onValueChange={handleTenantChange}
            >
              <SelectTrigger className="w-60">
                <SelectValue placeholder={t('sidebar.tenants')} />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map((opt: OptionItem) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>
        )}

        <DataTableFilterField
          label={t('common.search', { defaultValue: 'Search' })}
          className="min-w-[240px]"
        >
          <TenantSearchBar
            className="w-60"
            onSearch={(term) => {
              setSearchTerm(term)
              setPage(1)
            }}
          />
        </DataTableFilterField>

        <FilterPopover
          title={t('common.filter')}
          resetLabel={t('common.reset')}
          activeCount={activeFilterCount}
          onReset={resetAdvancedFilters}
        >
          <DataTableFilterField label={t('common.language')}>
            <Select
              value={selectedLanguageCode || defaultLanguageCode}
              onValueChange={(value) => {
                setSelectedLanguageCode(value)
                setPage(1)
              }}
              disabled={languageOptions.length < 1}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.language')} />
              </SelectTrigger>
              <SelectContent onViewportScroll={handleLanguageScroll}>
                {languageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('destination.country')}>
            <Select
              {...(selectedCountryId
                ? { value: selectedCountryId }
                : { value: POI_FILTER_VALUE.ALL })}
              onValueChange={(value) => {
                setSelectedCountryId(value === POI_FILTER_VALUE.ALL ? '' : value)
                setSelectedProvinceId('')
                setSelectedTenantDestinationId('')
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('destination.country')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={POI_FILTER_VALUE.ALL}>{t('common.all')}</SelectItem>
                {countryOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('destination.province')}>
            <Select
              {...(selectedProvinceId
                ? { value: selectedProvinceId }
                : { value: POI_FILTER_VALUE.ALL })}
              onValueChange={(value) => {
                setSelectedProvinceId(value === POI_FILTER_VALUE.ALL ? '' : value)
                setSelectedTenantDestinationId('')
                setPage(1)
              }}
              disabled={!selectedCountryId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('destination.province')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={POI_FILTER_VALUE.ALL}>{t('common.all')}</SelectItem>
                {provinceOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('poi.destination')}>
            <Select
              value={selectedTenantDestinationId || POI_FILTER_VALUE.ALL}
              onValueChange={(value) => {
                setSelectedTenantDestinationId(value === POI_FILTER_VALUE.ALL ? '' : value)
                setPage(1)
              }}
              disabled={!currentTenantId || destinationOptions.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('poi.destination')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={POI_FILTER_VALUE.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                {destinationOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('poi.category')}>
            <Select
              {...(category ? { value: category } : { value: POI_FILTER_VALUE.ALL })}
              onValueChange={(value) => {
                setCategory(value === POI_FILTER_VALUE.ALL ? '' : value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('poi.category')} />
              </SelectTrigger>
              <SelectContent onViewportScroll={handleCategoryScroll}>
                <SelectItem value={POI_FILTER_VALUE.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                {categoryOptions.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    <span className="flex min-w-0 items-center gap-2">
                      {item.icon && (
                        <img
                          src={item.icon}
                          alt=""
                          className="h-4 w-4 shrink-0 rounded-sm object-cover"
                          loading="lazy"
                        />
                      )}
                      <span className="truncate">{item.label}</span>
                    </span>
                  </SelectItem>
                ))}
                {poiCategoriesQuery.isFetchingNextPage && (
                  <div className="flex items-center justify-center py-2">
                    <span className="text-xs text-zinc-400">
                      {t('common.loading', { defaultValue: 'Đang tải...' })}
                    </span>
                  </div>
                )}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('common.status')}>
            <Select
              value={statusFilter}
              onValueChange={(value: PoiStatusFilter) => {
                setStatusFilter(value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={POI_STATUS_FILTER.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                <SelectItem value={POI_STATUS_FILTER.ACTIVE}>
                  {t('poi.activity.active', { defaultValue: 'Đang hoạt động' })}
                </SelectItem>
                <SelectItem value={POI_STATUS_FILTER.INACTIVE}>
                  {t('poi.activity.inactive', { defaultValue: 'Đã tắt' })}
                </SelectItem>
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('poi.createdSource')}>
            <Select
              value={createdSource || POI_FILTER_VALUE.ALL}
              onValueChange={(value: PoiCreatedSource | typeof POI_FILTER_VALUE.ALL) => {
                setCreatedSource(value === POI_FILTER_VALUE.ALL ? '' : value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('poi.createdSource')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={POI_FILTER_VALUE.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                <SelectItem value={POI_CREATED_SOURCE.TENANT_ADMIN}>
                  {t('poi.createdSourceOptions.tenantAdmin')}
                </SelectItem>
                <SelectItem value={POI_CREATED_SOURCE.CRAWL_DATA_SYSTEM}>
                  {t('poi.createdSourceOptions.crawlDataSystem')}
                </SelectItem>
              </SelectContent>
            </Select>
          </DataTableFilterField>
        </FilterPopover>
      </DataTableToolbar>

      <PoiListTable
        pois={pois}
        loading={currentTenantId ? isLoading : false}
        pagination={{ current: page, total, pageSize }}
        languageCode={selectedLanguageCode || defaultLanguageCode}
        onPageChange={handlePageChange}
        onEdit={handleEdit}
      />
    </PageContainer>
  )
}
